"use client";

import { useEffect } from "react";
import { useToolDock, type ToolId } from "./hooks/useToolDock";
import { useWindowStore } from "./windows/windowStore";
import WindowLayer from "./windows/WindowLayer";
import {
  defaultToolOrder,
  defaultToolRegistry,
  type ToolOverride,
  type ToolsDockThemeOptions,
} from "./tools/registry";

export type ToolsDockProps = {
  tools?: ToolId[]; // which tools to show, in order
  overrides?: Partial<Record<ToolId, ToolOverride>>; // per-tool label/window overrides
  theme?: ToolsDockThemeOptions; // initial + persistence options
};

export default function ToolsDock(props: ToolsDockProps = {}) {
  const dock = useToolDock();
  const createWindow = useWindowStore((s) => s.createWindow);
  const getByType = useWindowStore((s) => s.getByType);

  const tools: ToolId[] =
    props.tools && props.tools.length > 0 ? props.tools : defaultToolOrder;
  const overrides = props.overrides || {};
  const showThemeToggle = props.theme?.showToggle !== false;

  const handleToolClick = (tool: ToolId) => {
    dock.toggleTool(tool);
  };

  // Theme initialization and toggling
  useEffect(() => {
    const root = document.documentElement;
    const opts = props.theme || {};
    const persist = opts.persist !== false; // default true
    const saved = (() => {
      if (!persist) return null;
      try {
        return localStorage.getItem("theme");
      } catch {
        return null;
      }
    })();
    const systemPrefersDark =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const desired =
      (saved as "light" | "dark" | null) ||
      (opts.initial === "system" || !opts.initial
        ? systemPrefersDark
          ? "dark"
          : "light"
        : opts.initial);
    if (desired) {
      root.setAttribute("data-theme", desired);
    }
  }, [props.theme]);

  const toggleTheme = () => {
    const root = document.documentElement;
    const next = root.getAttribute("data-theme") === "dark" ? "light" : "dark";
    root.setAttribute("data-theme", next);
    const persist = props.theme?.persist !== false;
    if (persist) {
      try {
        localStorage.setItem("theme", next);
      } catch {}
    }
  };

  // Compute embed flag after hooks to avoid conditional hook calls
  const isEmbedMagnifier =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embedMagnifier") === "1";
  if (isEmbedMagnifier) {
    return null;
  }

  return (
    <div
      aria-label="Tools dock"
      className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-center pointer-events-none"
    >
      <div className="pointer-events-auto mb-4 rounded-full border border-border bg-background/80 backdrop-blur px-2 py-1 shadow-lg flex gap-1 items-center">
        {showThemeToggle && (
          <button
            className="h-9 px-3 rounded-full bg-surface hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            Theme
          </button>
        )}
        {tools.map((toolId) => {
          const def = defaultToolRegistry[toolId];
          if (!def) return null;
          const override = overrides[toolId];
          const label = override?.label ?? def.label;
          const ariaPressed =
            dock.state.isOpen && dock.state.activeTool === toolId;
          return (
            <button
              key={toolId}
              className="h-10 px-4 rounded-full bg-surface hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              onClick={() => {
                handleToolClick(toolId);
                const existing = getByType(toolId);
                if (!existing) {
                  const baseWin = def.window;
                  const windowParams = {
                    type: toolId,
                    ...baseWin,
                    ...override?.window,
                  } as import("./windows/windowTypes").CreateWindowParams;
                  createWindow(windowParams);
                }
              }}
              aria-pressed={ariaPressed}
              aria-label={`Toggle ${label.toLowerCase()}`}
            >
              {label}
            </button>
          );
        })}
      </div>
      <WindowLayer />
    </div>
  );
}
